import * as PIXI from 'pixi.js';

interface EdgeInfo {
  graphics: PIXI.Graphics;
  fromNode: number;
  toNode: number;
  layerConnection: string;
  weight?: number;
  fromPosition: { x: number; y: number };
  toPosition: { x: number; y: number };
}

interface NodeInfo {
  sprite: PIXI.Graphics;
  layerName: string;
  nodeIndex: number;
}

export default class PixiNetworkRendererClass {
  private container: HTMLElement;
  private app!: PIXI.Application;
  private nodes: NodeInfo[] = [];
  private edges: EdgeInfo[] = [];
  private nodeGraphics: PIXI.Graphics[] = [];
  private animationSpeed = 1.0;
  private layerNodeIndices: Map<string, number[]> = new Map();
  private isInitialized = false;
  private initializationPromise: Promise<void>;

  // Animation state properties
  private isAnimating = false;
  private animationStartTime = 0;
  private currentTrainingData: any = null;
  private layerOrder: string[] = [];
  private animationDuration = 1000; // Total animation duration in ms
  private endAnimationDelay = 500;
  private isWaitingForNextEpoch = false;
  private isBackpropMode = false;
  private backpropStartTime = 0;

  // Camera/viewport properties
  private panX = 0;
  private panY = 0;
  private zoomLevel = 1;
  private isDragging = false;
  private lastMouseX = 0;
  private lastMouseY = 0;

  // Stage container for pan/zoom
  private stageContainer!: PIXI.Container;

  constructor(container: HTMLElement) {
    this.container = container;
    this.initializationPromise = this.initializeApp();
  }

  private async initializeApp(): Promise<void> {
    try {
      console.log('[PixiRenderer] Starting initialization...');
      // Initialize Pixi Application using modern API
      this.app = new PIXI.Application();
      console.log('[PixiRenderer] Application created');

      await this.app.init({
        width: this.container.clientWidth,
        height: this.container.clientHeight,
        backgroundColor: 0x1a1a1a,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
      });

      console.log('[PixiRenderer] App initialized');
      this.container.appendChild(this.app.canvas as HTMLCanvasElement);
      console.log('[PixiRenderer] Canvas appended');

      // Create stage container for pan/zoom
      this.stageContainer = new PIXI.Container();
      this.app.stage.addChild(this.stageContainer);
      console.log('[PixiRenderer] Stage container created');

      // Setup event listeners
      this.setupEventListeners();
      console.log('[PixiRenderer] Event listeners setup');

      // Start animation loop
      this.animate();
      console.log('[PixiRenderer] Animation loop started');

      this.isInitialized = true;
      console.log('[PixiRenderer] Initialization complete');
    } catch (error) {
      console.error('[PixiNetworkRenderer] Failed to initialize:', error);
    }
  }

  // Public method to wait for initialization
  public async waitForInitialization(): Promise<void> {
    return this.initializationPromise;
  }

  private setupEventListeners() {
    const canvas = this.app.canvas as HTMLCanvasElement;

    // Mouse events for pan
    canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
    canvas.addEventListener('mousemove', (e) => this.onMouseMove(e));
    canvas.addEventListener('mouseup', () => this.onMouseUp());
    canvas.addEventListener('mouseleave', () => this.onMouseUp());

    // Wheel event for zoom
    canvas.addEventListener('wheel', (e) => this.onMouseWheel(e));

    // Handle window resize
    window.addEventListener('resize', () => this.handleResize());
  }

  private onMouseDown(e: MouseEvent) {
    if (e.button === 2 || e.ctrlKey) return; // Right click or Ctrl+click for context menu
    this.isDragging = true;
    this.lastMouseX = e.clientX;
    this.lastMouseY = e.clientY;
  }

  private onMouseMove(e: MouseEvent) {
    if (!this.isDragging) return;

    const deltaX = e.clientX - this.lastMouseX;
    const deltaY = e.clientY - this.lastMouseY;

    this.panX += deltaX / this.zoomLevel;
    this.panY += deltaY / this.zoomLevel;

    this.stageContainer.position.set(this.panX, this.panY);

    this.lastMouseX = e.clientX;
    this.lastMouseY = e.clientY;
  }

  private onMouseUp() {
    this.isDragging = false;
  }

  private onMouseWheel(e: WheelEvent) {
    e.preventDefault();

    const zoomSpeed = 0.1;
    const oldZoom = this.zoomLevel;
    this.zoomLevel += e.deltaY > 0 ? -zoomSpeed : zoomSpeed;
    this.zoomLevel = Math.max(0.1, Math.min(3, this.zoomLevel)); // Clamp zoom

    this.stageContainer.scale.set(this.zoomLevel, this.zoomLevel);
  }

  public setNetworkStructure(structure: any) {
    console.log('[PixiRenderer] setNetworkStructure called with structure:', structure);
    console.log('[PixiRenderer] Total layers:', structure?.layers?.length);

    // Clear existing nodes and edges
    console.log('[PixiRenderer] Clearing existing graphics...');
    this.nodeGraphics.forEach((node) => node.destroy());
    this.edges.forEach((edge) => edge.graphics.destroy());
    this.nodes = [];
    this.edges = [];
    this.nodeGraphics = [];
    this.layerNodeIndices.clear();
    this.layerOrder = [];
    console.log('[PixiRenderer] Cleared existing graphics');

    const layerSpacing = 150;
    const nodeSpacing = 40;

    // Determine visual layers
    const visualLayers: { type: string; nodeCount: number; name?: string }[] = [];

    console.log('[PixiRenderer] Processing layers...');
    for (let i = 0; i < structure.layers.length; i++) {
      const layer = structure.layers[i];
      console.log('[PixiRenderer] Processing layer', i, 'name:', layer.name);

      let nodeCount = 32;
      if (layer.parameters) {
        nodeCount =
          layer.parameters.out_features ||
          layer.parameters.output_size ||
          layer.parameters.num_features ||
          layer.parameters.filters ||
          layer.parameters.channels ||
          32;
      }

      visualLayers.push({
        type:
          layer.name === 'input'
            ? 'input'
            : layer.name === 'output'
              ? 'output'
              : 'hidden',
        nodeCount: nodeCount,
        name: layer.name,
      });

      if (layer.name) {
        this.layerOrder.push(layer.name);
      }
    }

    // Calculate total width needed
    const totalWidth = visualLayers.length * layerSpacing;
    const startX = -totalWidth / 2;

    // Create nodes for each layer
    console.log('[PixiRenderer] Creating nodes for layers...');
    visualLayers.forEach((layer, layerIndex) => {
      console.log(`[PixiRenderer] Creating layer ${layerIndex} (${layer.name}) with ${layer.nodeCount} nodes`);
      const x = startX + layerIndex * layerSpacing;
      const nodeCount = layer.nodeCount;
      const totalHeight = nodeCount * nodeSpacing;
      const startY = -totalHeight / 2;

      const nodeIndices: number[] = [];

      for (let j = 0; j < nodeCount; j++) {
        const y = startY + j * nodeSpacing;
        const nodeGraphic = this.createNode(x, y, 8);
        const nodeIndex = this.nodeGraphics.length;
        nodeIndices.push(nodeIndex);

        this.nodeGraphics.push(nodeGraphic);
        this.stageContainer.addChild(nodeGraphic);

        this.nodes.push({
          sprite: nodeGraphic,
          layerName: layer.name || `layer-${layerIndex}`,
          nodeIndex: j,
        });
      }

      if (layer.name) {
        this.layerNodeIndices.set(layer.name, nodeIndices);
      }
      console.log(`[PixiRenderer] Created layer ${layerIndex}, total nodes so far: ${this.nodeGraphics.length}`);
    });

    // Create connections between layers
    const layerPositions: { [key: string]: { x: number; y: number }[] } = {};

    visualLayers.forEach((layer, layerIndex) => {
      const x = startX + layerIndex * layerSpacing;
      const nodeCount = layer.nodeCount;
      const totalHeight = nodeCount * nodeSpacing;
      const startY = -totalHeight / 2;

      const positions: { x: number; y: number }[] = [];
      for (let j = 0; j < nodeCount; j++) {
        const y = startY + j * nodeSpacing;
        positions.push({ x, y });
      }

      if (layer.name) {
        layerPositions[layer.name] = positions;
      }
    });

    // Create edges - Full connections between all nodes in consecutive layers
    console.log('[PixiRenderer] Creating edges between layers...');
    let totalEdgesCreated = 0;

    for (let i = 0; i < visualLayers.length - 1; i++) {
      const fromLayer = visualLayers[i];
      const toLayer = visualLayers[i + 1];

      if (fromLayer.name && toLayer.name) {
        const fromPositions = layerPositions[fromLayer.name];
        const toPositions = layerPositions[toLayer.name];
        const connectionName = `${fromLayer.name}-${toLayer.name}`;

        const fromIndices = this.layerNodeIndices.get(fromLayer.name) || [];
        const toIndices = this.layerNodeIndices.get(toLayer.name) || [];

        console.log(`[PixiRenderer] Creating edges from layer ${i} (${fromPositions.length} nodes) to layer ${i + 1} (${toPositions.length} nodes)`);
        let edgesThisConnection = 0;

        // Create connections between ALL nodes in consecutive layers
        for (let fromIdx = 0; fromIdx < fromPositions.length; fromIdx++) {
          const fromPos = fromPositions[fromIdx];
          const fromNodeIndex = fromIndices[fromIdx];

          for (let toIdx = 0; toIdx < toPositions.length; toIdx++) {
            const toPos = toPositions[toIdx];
            const toNodeIndex = toIndices[toIdx];

            const edgeGraphic = this.createEdge(fromPos, toPos);
            this.stageContainer.addChild(edgeGraphic);

            this.edges.push({
              graphics: edgeGraphic,
              fromNode: fromNodeIndex,
              toNode: toNodeIndex,
              layerConnection: connectionName,
              fromPosition: fromPos,
              toPosition: toPos,
            });

            edgesThisConnection++;
            totalEdgesCreated++;
          }
        }

        console.log(`[PixiRenderer] Created ${edgesThisConnection} edges for connection ${i}->${i + 1}`);
      }
    }
    console.log(`[PixiRenderer] Total edges created: ${totalEdgesCreated}`);

    // Fit viewport to all content
    this.fitViewToContent();
    console.log(
      `[2D Pixi VIZ] Created network with ${this.nodeGraphics.length} nodes and ${this.edges.length} edges`
    );
  }

  private createNode(x: number, y: number, radius: number): PIXI.Graphics {
    const node = new PIXI.Graphics();
    // Modern Pixi.js v8 API
    node.circle(0, 0, radius);
    node.fill(0x555555);
    node.position.set(x, y);
    return node;
  }

  private createEdge(
    fromPos: { x: number; y: number },
    toPos: { x: number; y: number }
  ): PIXI.Graphics {
    const edge = new PIXI.Graphics();
    // Modern Pixi.js v8 API: setStrokeStyle and stroke/moveTo/lineTo chain
    edge.setStrokeStyle({
      width: 1,
      color: 0x888888,
      alpha: 0.2,
    });
    edge.moveTo(fromPos.x, fromPos.y);
    edge.lineTo(toPos.x, toPos.y);
    edge.stroke();
    return edge;
  }

  private fitViewToContent() {
    if (this.nodeGraphics.length === 0) {
      this.stageContainer.position.set(0, 0);
      this.stageContainer.scale.set(1, 1);
      return;
    }

    // Get bounds of all content
    const bounds = this.stageContainer.getLocalBounds();
    const containerWidth = this.app.canvas.width;
    const containerHeight = this.app.canvas.height;

    // Calculate zoom to fit content with padding
    const padding = 50;
    const scaleX = (containerWidth - padding * 2) / bounds.width;
    const scaleY = (containerHeight - padding * 2) / bounds.height;
    const newZoom = Math.min(scaleX, scaleY, 1.5);

    this.zoomLevel = newZoom;
    this.stageContainer.scale.set(newZoom, newZoom);

    // Center content
    const scaledBounds = this.stageContainer.getLocalBounds();
    this.panX =
      containerWidth / 2 - (bounds.x + bounds.width / 2) * newZoom;
    this.panY =
      containerHeight / 2 - (bounds.y + bounds.height / 2) * newZoom;
    this.stageContainer.position.set(this.panX, this.panY);
  }

  public updateTrainingData(data: any) {
    this.currentTrainingData = data;
    this.resetVisualizationState();
    this.isAnimating = true;
    this.animationStartTime = performance.now();
  }

  private resetVisualizationState() {
    // Reset all nodes to gray
    this.nodeGraphics.forEach((node) => {
      node.clear();
      node.circle(0, 0, 8);
      node.fill(0x555555);
    });

    // Reset all edges to gray
    this.edges.forEach((edge) => {
      edge.graphics.clear();
      edge.graphics.setStrokeStyle({
        width: 1,
        color: 0x888888,
        alpha: 0.2,
      });
      edge.graphics.moveTo(edge.fromPosition.x, edge.fromPosition.y);
      edge.graphics.lineTo(edge.toPosition.x, edge.toPosition.y);
      edge.graphics.stroke();
    });
  }

  private animate = () => {
    const currentTime = performance.now();
    this.processAnimation(currentTime);
    this.app.render();
    requestAnimationFrame(this.animate);
  };

  private processAnimation(currentTime: number) {
    if (!this.isAnimating || !this.currentTrainingData) return;

    const elapsedTime = currentTime - this.animationStartTime;
    const totalDuration = this.animationDuration + this.endAnimationDelay;

    // Check if we're in the delay phase at the end of the animation
    if (
      elapsedTime >= this.animationDuration &&
      !this.isWaitingForNextEpoch &&
      !this.isBackpropMode
    ) {
      this.isBackpropMode = true;
      this.backpropStartTime = currentTime;
      this.applyFinalState();
      return;
    }

    // Check if we're in backprop mode
    if (this.isBackpropMode) {
      const backpropElapsedTime = currentTime - this.backpropStartTime;

      if (backpropElapsedTime >= this.animationDuration) {
        this.isWaitingForNextEpoch = true;
        this.isBackpropMode = false;
      } else {
        this.processBackpropAnimation(backpropElapsedTime);
      }

      if (
        this.isWaitingForNextEpoch &&
        currentTime - this.backpropStartTime >=
          this.animationDuration + this.endAnimationDelay
      ) {
        this.isAnimating = false;
        this.isWaitingForNextEpoch = false;
      }

      return;
    }

    // Check if the entire animation cycle is complete
    if (elapsedTime >= totalDuration && !this.isBackpropMode) {
      this.isAnimating = false;
      this.isWaitingForNextEpoch = false;
      return;
    }

    // Process forward animation
    if (elapsedTime < this.animationDuration && !this.isBackpropMode) {
      const progress = elapsedTime / this.animationDuration;
      const layerCount = this.layerOrder.length;

      const segmentLength = 1.0 / layerCount;
      const activeLayerIndex = Math.min(
        Math.floor(progress / segmentLength),
        layerCount - 1
      );

      const layerProgress =
        (progress - activeLayerIndex * segmentLength) / segmentLength;

      this.animateLayersSequentially(activeLayerIndex, layerProgress);
    }
  }

  private processBackpropAnimation(elapsedTime: number) {
    const progress = elapsedTime / this.animationDuration;
    const layerCount = this.layerOrder.length;

    const segmentLength = 1.0 / layerCount;
    const activeLayerIndex = Math.min(
      Math.floor(progress / segmentLength),
      layerCount - 1
    );
    const reverseLayerIndex = layerCount - 1 - activeLayerIndex;

    const layerProgress =
      (progress - activeLayerIndex * segmentLength) / segmentLength;

    this.animateBackpropSequentially(reverseLayerIndex, layerProgress);
  }

  private animateLayersSequentially(
    activeLayerIndex: number,
    layerProgress: number
  ) {
    const data = this.currentTrainingData;
    if (!data || !data.activations) return;

    const activationsMap = new Map<string, number[]>();
    Object.entries(data.activations).forEach(([layerName, values]) => {
      if (Array.isArray(values)) {
        activationsMap.set(layerName, values as number[]);
      }
    });

    // Fully activate all previous layers
    for (let i = 0; i < activeLayerIndex; i++) {
      const layerName = this.layerOrder[i];
      this.animateLayerNodes(layerName, activationsMap, 1.0);

      if (i < activeLayerIndex - 1) {
        const nextLayerName = this.layerOrder[i + 1];
        this.animateLayerConnections(
          layerName,
          nextLayerName,
          activationsMap,
          1.0
        );
      }
    }

    // Handle active layer nodes
    const currentLayerName = this.layerOrder[activeLayerIndex];
    this.animateLayerNodes(currentLayerName, activationsMap, 1.0);

    // Handle connections from active to next layer
    if (activeLayerIndex < this.layerOrder.length - 1) {
      const nextLayerName = this.layerOrder[activeLayerIndex + 1];
      const connectionKey = `${currentLayerName}-${nextLayerName}`;

      const signalSpeed = 4.0;
      const layerTime = this.animationDuration / this.layerOrder.length;
      const timeElapsed = layerProgress * layerTime;
      const timeInSeconds = timeElapsed / 1000;
      const maxDistance = timeInSeconds * signalSpeed;

      this.edges.forEach((edge) => {
        if (edge.layerConnection === connectionKey) {
          if (!data?.weights || !activationsMap.has(currentLayerName))
            return;

          const weightKey = `${nextLayerName}.weight`;
          if (!data.weights[weightKey]) return;

          const fromActivations = activationsMap.get(currentLayerName);
          const toActivations = activationsMap.get(nextLayerName);

          if (!fromActivations || !toActivations) return;

          const fromLayerIndices =
            this.layerNodeIndices.get(currentLayerName) || [];
          const toLayerIndices = this.layerNodeIndices.get(nextLayerName) || [];

          const fromLayerIndex = fromLayerIndices.indexOf(edge.fromNode);
          const toLayerIndex = toLayerIndices.indexOf(edge.toNode);

          if (
            fromLayerIndex < 0 ||
            toLayerIndex < 0 ||
            !data.weights[weightKey][toLayerIndex] ||
            fromLayerIndex >= fromActivations.length
          )
            return;

          const weight = data.weights[weightKey][toLayerIndex][fromLayerIndex];
          const activation = fromActivations[fromLayerIndex];

          const distance = Math.hypot(
            edge.toPosition.x - edge.fromPosition.x,
            edge.toPosition.y - edge.fromPosition.y
          );
          const connectionProgress = Math.min(1.0, maxDistance / distance);

          this.animateConnectionSegments(
            edge,
            weight,
            activation,
            connectionProgress
          );

          // Update next layer nodes
          if (connectionProgress >= 1.0) {
            if (toLayerIndex < toActivations.length) {
              const toActivation = toActivations[toLayerIndex];
              this.updateNodeAppearance(
                edge.toNode,
                toActivation,
                Math.min(...toActivations),
                Math.max(...toActivations),
                0.0
              );
            }
          }
        }
      });
    }
  }

  private animateBackpropSequentially(
    activeLayerIndex: number,
    layerProgress: number
  ) {
    const data = this.currentTrainingData;
    if (!data || !data.activations) return;

    const activationsMap = new Map<string, number[]>();
    Object.entries(data.activations).forEach(([layerName, values]) => {
      if (Array.isArray(values)) {
        activationsMap.set(layerName, values as number[]);
      }
    });

    // Fully activate all layers to the right
    for (let i = this.layerOrder.length - 1; i > activeLayerIndex; i--) {
      const layerName = this.layerOrder[i];
      this.animateLayerNodesBackprop(layerName, activationsMap, 1.0);

      if (i < this.layerOrder.length - 1) {
        const nextLayerName = this.layerOrder[i + 1];
        this.animateLayerConnectionsBackprop(
          layerName,
          nextLayerName,
          activationsMap,
          1.0
        );
      }
    }

    // Handle active layer
    const currentLayerName = this.layerOrder[activeLayerIndex];
    this.animateLayerNodesBackprop(currentLayerName, activationsMap, 1.0);

    if (activeLayerIndex > 0) {
      const prevLayerName = this.layerOrder[activeLayerIndex - 1];
      const connectionKey = `${prevLayerName}-${currentLayerName}`;

      const signalSpeed = 4.0;
      const layerTime = this.animationDuration / this.layerOrder.length;
      const timeElapsed = layerProgress * layerTime;
      const timeInSeconds = timeElapsed / 1000;
      const maxDistance = timeInSeconds * signalSpeed;

      this.edges.forEach((edge) => {
        if (edge.layerConnection === connectionKey) {
          if (!data?.weights || !activationsMap.has(currentLayerName))
            return;

          const weightKey = `${currentLayerName}.weight`;
          if (!data.weights[weightKey]) return;

          const fromActivations = activationsMap.get(prevLayerName);
          const toActivations = activationsMap.get(currentLayerName);

          if (!fromActivations || !toActivations) return;

          const fromLayerIndex = fromActivations.length > 0 ? 0 : -1;
          const toLayerIndex = toActivations.length > 0 ? 0 : -1;

          if (fromLayerIndex < 0 || toLayerIndex < 0) return;

          const weight = data.weights[weightKey][toLayerIndex][fromLayerIndex];
          const activation = toActivations[toLayerIndex];

          const distance = Math.hypot(
            edge.toPosition.x - edge.fromPosition.x,
            edge.toPosition.y - edge.fromPosition.y
          );
          const connectionProgress = Math.min(1.0, maxDistance / distance);

          this.animateConnectionSegmentsBackprop(
            edge,
            weight,
            activation,
            connectionProgress
          );
        }
      });
    }
  }

  private animateLayerNodes(
    layerName: string,
    activationsMap: Map<string, number[]>,
    progress: number
  ) {
    if (!activationsMap.has(layerName)) return;

    const layerActivations = activationsMap.get(layerName)!;
    const minActivation = Math.min(...layerActivations);
    const maxActivation = Math.max(...layerActivations);

    const indices = this.layerNodeIndices.get(layerName) || [];
    indices.forEach((nodeIndex, nodePosition) => {
      if (nodePosition < layerActivations.length) {
        this.updateNodeAppearance(
          nodeIndex,
          layerActivations[nodePosition],
          minActivation,
          maxActivation,
          progress
        );
      }
    });
  }

  private animateLayerNodesBackprop(
    layerName: string,
    activationsMap: Map<string, number[]>,
    progress: number
  ) {
    if (!activationsMap.has(layerName)) return;

    const layerActivations = activationsMap.get(layerName)!;
    const minActivation = Math.min(...layerActivations);
    const maxActivation = Math.max(...layerActivations);

    const indices = this.layerNodeIndices.get(layerName) || [];
    indices.forEach((nodeIndex, nodePosition) => {
      if (nodePosition < layerActivations.length) {
        this.updateNodeAppearanceBackprop(
          nodeIndex,
          layerActivations[nodePosition],
          minActivation,
          maxActivation,
          progress
        );
      }
    });
  }

  private animateLayerConnections(
    fromLayerName: string,
    toLayerName: string,
    activationsMap: Map<string, number[]>,
    progress: number
  ) {
    const connectionKey = `${fromLayerName}-${toLayerName}`;
    const data = this.currentTrainingData;

    if (!data?.weights || !activationsMap.has(fromLayerName)) return;

    this.edges.forEach((edge) => {
      if (edge.layerConnection === connectionKey) {
        const weightKey = `${toLayerName}.weight`;
        if (!data.weights[weightKey]) return;

        const fromActivations = activationsMap.get(fromLayerName);
        const fromLayerIndices =
          this.layerNodeIndices.get(fromLayerName) || [];
        const toLayerIndices = this.layerNodeIndices.get(toLayerName) || [];

        const fromLayerIndex = fromLayerIndices.indexOf(edge.fromNode);
        const toLayerIndex = toLayerIndices.indexOf(edge.toNode);

        if (
          fromLayerIndex < 0 ||
          toLayerIndex < 0 ||
          !data.weights[weightKey][toLayerIndex] ||
          !fromActivations ||
          fromLayerIndex >= fromActivations.length
        )
          return;

        const weight = data.weights[weightKey][toLayerIndex][fromLayerIndex];
        const activation = fromActivations[fromLayerIndex];

        this.animateConnectionSegments(edge, weight, activation, progress);
      }
    });
  }

  private animateLayerConnectionsBackprop(
    fromLayerName: string,
    toLayerName: string,
    activationsMap: Map<string, number[]>,
    progress: number
  ) {
    const connectionKey = `${fromLayerName}-${toLayerName}`;
    const data = this.currentTrainingData;

    if (!data?.weights || !activationsMap.has(fromLayerName)) return;

    this.edges.forEach((edge) => {
      if (edge.layerConnection === connectionKey) {
        const weightKey = `${toLayerName}.weight`;
        if (!data.weights[weightKey]) return;

        const fromActivations = activationsMap.get(fromLayerName);
        const fromLayerIndices =
          this.layerNodeIndices.get(fromLayerName) || [];
        const toLayerIndices = this.layerNodeIndices.get(toLayerName) || [];

        const fromLayerIndex = fromLayerIndices.indexOf(edge.fromNode);
        const toLayerIndex = toLayerIndices.indexOf(edge.toNode);

        if (
          fromLayerIndex < 0 ||
          toLayerIndex < 0 ||
          !data.weights[weightKey][toLayerIndex] ||
          !fromActivations ||
          fromLayerIndex >= fromActivations.length
        )
          return;

        const weight = data.weights[weightKey][toLayerIndex][fromLayerIndex];
        const activation = fromActivations[fromLayerIndex];

        this.animateConnectionSegmentsBackprop(
          edge,
          weight,
          activation,
          progress
        );
      }
    });
  }

  private animateConnectionSegments(
    edge: EdgeInfo,
    weight: number,
    activation: number,
    progress: number
  ) {
    const signalStrength = weight * activation;
    const absSignalStrength = Math.abs(signalStrength);
    const normalizedStrength = Math.min(absSignalStrength, 1.0);

    const color =
      signalStrength >= 0
        ? ((0xff << 16) | (0xaa << 8) | 0x00) // Orange
        : ((0xaa << 16) | (0x00 << 8) | 0xff); // Purple

    edge.graphics.clear();
    edge.graphics.setStrokeStyle({
      width: 2,
      color: color,
      alpha: (0.1 + normalizedStrength * 0.9) * progress,
    });
    edge.graphics.moveTo(edge.fromPosition.x, edge.fromPosition.y);
    edge.graphics.lineTo(edge.toPosition.x, edge.toPosition.y);
    edge.graphics.stroke();
  }

  private animateConnectionSegmentsBackprop(
    edge: EdgeInfo,
    weight: number,
    activation: number,
    progress: number
  ) {
    const signalStrength = weight * activation;
    const absSignalStrength = Math.abs(signalStrength);
    const normalizedStrength = Math.min(absSignalStrength, 1.0);

    const color = 0x00bfff; // Light blue for backprop

    edge.graphics.clear();
    edge.graphics.setStrokeStyle({
      width: 2,
      color: color,
      alpha: (0.1 + normalizedStrength * 0.9) * progress,
    });
    edge.graphics.moveTo(edge.fromPosition.x, edge.fromPosition.y);
    edge.graphics.lineTo(edge.toPosition.x, edge.toPosition.y);
    edge.graphics.stroke();
  }

  private updateNodeAppearance(
    nodeIndex: number,
    activation: number,
    minActivation: number,
    maxActivation: number,
    progress: number
  ) {
    const node = this.nodeGraphics[nodeIndex];
    if (!node) return;

    const normalizedActivation =
      (activation - minActivation) /
      Math.max(maxActivation - minActivation, 0.01);
    let brightness = 0.1 + Math.min(Math.abs(normalizedActivation), 1) * 0.9;

    const color = new PIXI.Color(0xffaa00); // Orange for forward pass

    node.clear();
    node.circle(0, 0, 8);
    node.fill(color.toNumber());

    // Adjust alpha based on activation
    node.alpha = 0.3 + brightness * 0.7;
  }

  private updateNodeAppearanceBackprop(
    nodeIndex: number,
    activation: number,
    minActivation: number,
    maxActivation: number,
    progress: number = 1.0
  ) {
    const node = this.nodeGraphics[nodeIndex];
    if (!node) return;

    const normalizedActivation =
      (activation - minActivation) /
      Math.max(maxActivation - minActivation, 0.01);
    let brightness = 0.1 + Math.min(Math.abs(normalizedActivation), 1) * 0.9;

    const color = new PIXI.Color(0x00bfff); // Light blue for backprop

    node.clear();
    node.circle(0, 0, 8);
    node.fill(color.toNumber());

    node.alpha = 0.3 + brightness * 0.7 * progress;
  }

  private applyFinalState() {
    const data = this.currentTrainingData;
    if (!data || !data.activations) return;

    const activationsMap = new Map<string, number[]>();
    Object.entries(data.activations).forEach(([layerName, values]) => {
      if (Array.isArray(values)) {
        activationsMap.set(layerName, values as number[]);
      }
    });

    // Show final state of forward pass
    this.layerOrder.forEach((layerName) => {
      if (activationsMap.has(layerName)) {
        this.animateLayerNodes(layerName, activationsMap, 1.0);
      }
    });
  }

  public handleResize() {
    const width = this.container.clientWidth;
    const height = this.container.clientHeight;
    this.app.renderer.resize(width, height);
  }

  public cleanup() {
    this.nodeGraphics.forEach((node) => node.destroy());
    this.edges.forEach((edge) => edge.graphics.destroy());
    this.stageContainer.destroy();
    this.app.destroy();
  }
}
